import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import Cookies from "js-cookie"

import useSlugExtractor from "@hooks/useSlugExtractor"

import replaceSlugs from "@utils/replaceSlugs"

import toast from "@molecules/Toast/Toast"

import { CATALOG_ENDPOINTS } from "@endpoints/catalogs"
import { PRICEBOOK_ENDPOINTS } from "@endpoints/pricebook"

import { PriceBookItemCatalogLinkDetails } from "./CatalogLinkSection.types"

interface PriceBookItemWithCatalogLinks {
    catalog_links?: PriceBookItemCatalogLinkDetails[]
}

async function sendCatalogLinkAction(endpoint: string): Promise<PriceBookItemCatalogLinkDetails> {
    const response = await fetch(endpoint, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "X-CSRFToken": Cookies.get("csrftoken") ?? "",
        },
    })

    if (!response.ok) {
        throw new Error(`Request to ${endpoint} failed with status ${response.status}.`)
    }

    return (await response.json()) as PriceBookItemCatalogLinkDetails
}

export function useCatalogLinksQuery(pricebookItemId: string) {
    const [detailsEndpointTemplate] = useSlugExtractor([PRICEBOOK_ENDPOINTS.DETAILS])
    const detailsEndpoint = replaceSlugs(detailsEndpointTemplate, { id: pricebookItemId })

    return useQuery({
        queryKey: ["pricebook-item-catalog-links", detailsEndpoint],
        queryFn: async () => {
            const response = await fetch(detailsEndpoint)

            if (!response.ok) {
                throw new Error(`Request to ${detailsEndpoint} failed with status ${response.status}.`)
            }

            const pricebookItem = (await response.json()) as PriceBookItemWithCatalogLinks
            return pricebookItem.catalog_links ?? []
        },
    })
}

export function useInvalidateCatalogLinks(pricebookItemId: string) {
    const queryClient = useQueryClient()
    const [detailsEndpointTemplate] = useSlugExtractor([PRICEBOOK_ENDPOINTS.DETAILS])
    const detailsEndpoint = replaceSlugs(detailsEndpointTemplate, { id: pricebookItemId })

    return () => queryClient.invalidateQueries({ queryKey: ["pricebook-item-catalog-links", detailsEndpoint] })
}

export function useCatalogLinkDisconnectMutation() {
    const [disconnectEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.CATALOG_LINK_DISCONNECT])

    return useMutation<PriceBookItemCatalogLinkDetails, Error, PriceBookItemCatalogLinkDetails>({
        mutationFn: (link) => sendCatalogLinkAction(replaceSlugs(disconnectEndpointTemplate, { id: String(link.id) })),
        onSuccess: (data, link) => {
            toast({ type: "success", title: `Disconnected from ${link.catalog_item.name}` })
        },
        onError: () => {
            toast({ type: "error", title: "Could not disconnect the catalog item" })
        },
    })
}

export function useCatalogLinkReconnectMutation() {
    const [reconnectEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.CATALOG_LINK_RECONNECT])

    return useMutation<PriceBookItemCatalogLinkDetails, Error, PriceBookItemCatalogLinkDetails>({
        mutationFn: (link) => sendCatalogLinkAction(replaceSlugs(reconnectEndpointTemplate, { id: String(link.id) })),
        onSuccess: (data, link) => {
            toast({ type: "success", title: `Reconnected to ${link.catalog_item.name}` })
        },
        onError: () => {
            toast({ type: "error", title: "Could not reconnect the catalog item" })
        },
    })
}
